export const API_VERSION = 25;

export type AnalysisModelId = "england_wales" | "northern_ireland" | "scotland";

export interface MapTileSpec {
  readonly minZoom: number;
  readonly maxZoom: number;
}

export interface BasemapSpec {
  readonly minZoom: number;
  readonly maxZoom: number;
}

export type StatisticProvenance =
  | { readonly kind: "ons_census" }
  | { readonly kind: "nrs_census" }
  | { readonly kind: "nisra_census" }
  | { readonly kind: "uk_parliament" }
  | {
      readonly kind: "council_election";
      readonly geography: "council_ward" | "county_division";
    }
  | { readonly kind: "eu_referendum" };

export interface StatisticSpec {
  readonly id: string;
  readonly table: string;
  readonly label: string;
  readonly file: string;
  readonly denominatorColumn: string | null;
  readonly provenance: StatisticProvenance;
}

export interface PointLayerSpec {
  readonly id: string;
  readonly label: string;
  readonly recordCount: number;
  readonly addressableRecordCount: number;
  readonly geocodedRecordCount: number;
  readonly locationCount: number;
}

export interface ElectoralRegisterSpec {
  readonly id: string;
  readonly label: string;
  readonly electorCount: number;
  readonly addressCount: number;
  readonly geocodedElectorCount: number;
  readonly geocodedAddressCount: number;
  readonly baseAreaAssignedElectorCount: number;
  readonly baseAreaAssignedAddressCount: number;
  readonly usrnAssignedElectorCount: number;
  readonly usrnAssignedAddressCount: number;
  readonly locationCount: number;
}

export interface AffinitySubgroup {
  readonly enabled: boolean;
  readonly statisticIds: readonly string[];
}

export interface AffinityGroup {
  readonly name: string;
  readonly weightPercent: number;
  readonly subgroups: readonly AffinitySubgroup[];
}

export interface AffinityRecipe {
  readonly groups: readonly AffinityGroup[];
}

export function isAffinityWeight(value: unknown): value is number {
  return typeof value === "number" && Number.isInteger(value) && Math.abs(value) <= 100;
}

export interface OperationalModelParams {
  readonly serviceSecondsPerHousehold: number;
  readonly walkingSecondsAtReferenceDensity: number;
  readonly referenceDensityHouseholdsPerKm2: number;
  readonly fixedSetupSecondsPerBaseArea: number;
  readonly affinityContrastP90P10: number;
  readonly minimumEffectiveDensityHouseholdsPerKm2: number;
  readonly maximumEffectiveDensityHouseholdsPerKm2: number;
}

export type OperationalPreset = "canvassing" | "leafletting";

export interface OperationalPresetDefinition {
  readonly preset: OperationalPreset;
  readonly label: string;
  readonly parameters: OperationalModelParams;
}

export interface CatalogResponse {
  readonly apiVersion: number;
  readonly dataVersion: number;
  readonly baseAreaCount: number;
  readonly mapMin: readonly [number, number];
  readonly mapMax: readonly [number, number];
  readonly mapTiles: MapTileSpec;
  readonly basemap: BasemapSpec;
  readonly analysisModels: readonly AnalysisModelCatalog[];
  readonly pointLayers: readonly PointLayerSpec[];
  readonly electoralRegisters: readonly ElectoralRegisterSpec[];
  readonly operationalPresets: readonly OperationalPresetDefinition[];
}

export interface AnalysisModelCatalog {
  readonly id: AnalysisModelId;
  readonly label: string;
  readonly displayHierarchy: readonly DisplayHierarchyLevel[];
  readonly baseAreaCount: number;
  readonly statistics: readonly StatisticSpec[];
  readonly initialStatisticId: string;
  readonly defaultAffinityRecipe: AffinityRecipe;
}

export type OverlayId =
  | "lsoas"
  | "msoas"
  | "council_wards"
  | "county_divisions"
  | "constituencies"
  | "settlements"
  | "scotland_data_zones"
  | "scotland_intermediate_zones"
  | "northern_ireland_super_data_zones";

export interface DisplayHierarchyLevel {
  readonly overlay: OverlayId | null;
  readonly minZoom: number;
}

export interface StatisticSourceContext {
  readonly sourceAreaCode: string;
  readonly sourceAreaName: string;
  readonly sourceAuthorityName: string;
  readonly sourceElectionDate: string;
  readonly sourceContributorCount: number;
  readonly dominantSourceWeight: number;
}

export interface StatisticAggregationInputs {
  readonly numerators: readonly (number | null)[];
  readonly denominators: readonly (number | null)[] | null;
}

export interface OperationalInputRow {
  readonly affinityPercentile: number | null;
  readonly householdCount: number | null;
  readonly householdDensityPerKm2: number | null;
}

export interface MetricResponse {
  readonly model: AnalysisModelId;
  readonly label: string;
  readonly description: string;
  readonly percentiles: readonly (number | null)[];
  readonly sourceContexts: readonly (StatisticSourceContext | null)[] | null;
  readonly aggregationInputs: StatisticAggregationInputs | null;
  readonly operationalInputs: readonly OperationalInputRow[] | null;
  readonly completionHours: readonly (number | null)[] | null;
}

export interface MapMetadataResponse {
  readonly model: AnalysisModelId;
  readonly baseAreaMapIndices: readonly number[];
  readonly baseAreaKm2: readonly number[];
  readonly memberships: Readonly<Record<OverlayId, readonly (readonly number[])[]>>;
  readonly medianAges: MedianAgeMetadata;
}

export interface MedianAgeMetadata {
  readonly censusYear: number;
  readonly bandWidthYears: number;
  readonly topCodedAge: number;
  readonly baseAreas: readonly (number | null)[];
  readonly overlays: Readonly<Record<OverlayId, readonly (number | null)[]>>;
}

export interface MapArea {
  readonly index: number;
  readonly code: string;
  readonly name: string;
  readonly bounds: readonly [readonly [number, number], readonly [number, number]];
}

export interface MapAreasResponse {
  readonly model: AnalysisModelId;
  readonly areas: readonly MapArea[];
}

export function parseMapAreasResponse(value: unknown): MapAreasResponse {
  const record = exactObject(value, "map areas", ["model", "areas"]);
  const areas = array(record.areas, "map areas").map((value) => {
    const area = exactObject(value, "map area", ["index", "code", "name", "bounds"]);
    const bounds = array(area.bounds, "area bounds");
    if (bounds.length !== 2) throw new Error("Area bounds must contain two corners.");
    const min = numberPair(bounds[0], "area minimum");
    const max = numberPair(bounds[1], "area maximum");
    if (min[0] > max[0] || min[1] > max[1])
      throw new Error("Area bounds must run from minimum to maximum.");
    return {
      index: unsignedInteger(area.index, "area index", 0xffff_ffff),
      code: string(area.code, "area code", true),
      name: string(area.name, "area name"),
      bounds: [min, max] as const,
    };
  });
  unique(
    areas.map(({ index }) => index),
    "map area indices",
  );
  return {
    model: parseAnalysisModelId(record.model),
    areas,
  };
}

export type AreaGeography =
  | "base_area"
  | "lsoa"
  | "msoa"
  | "council_ward"
  | "county_division"
  | "constituency"
  | "settlement";

function parseOverlayId(value: unknown): OverlayId {
  return enumeration(
    value,
    [
      "lsoas",
      "msoas",
      "council_wards",
      "county_divisions",
      "constituencies",
      "settlements",
      "scotland_data_zones",
      "scotland_intermediate_zones",
      "northern_ireland_super_data_zones",
    ],
    "display hierarchy overlay",
  );
}

export interface ElectoralRegisterBootstrapResponse {
  readonly electorCountsByBaseArea: readonly number[];
}

export type MetricRequest =
  | {
      readonly kind: "statistic";
      readonly model: AnalysisModelId;
      readonly statisticId: string;
    }
  | {
      readonly kind: "affinity";
      readonly model: AnalysisModelId;
      readonly recipe: AffinityRecipe;
    }
  | {
      readonly kind: "operational_priority";
      readonly model: AnalysisModelId;
      readonly recipe: AffinityRecipe;
      readonly parameters: OperationalModelParams;
    };

export function parseCatalogResponse(value: unknown): CatalogResponse {
  const record = exactObject(value, "catalogue", [
    "api_version",
    "data_version",
    "base_area_count",
    "map_min",
    "map_max",
    "map_tiles",
    "basemap",
    "analysis_models",
    "point_layers",
    "electoral_registers",
    "operational_presets",
  ]);
  const analysisModels = array(record.analysis_models, "analysis models").map(
    parseAnalysisModel,
  );
  unique(
    analysisModels.map(({ id }) => id),
    "analysis model IDs",
  );
  unique(
    analysisModels.flatMap((model) =>
      model.displayHierarchy.flatMap((level) =>
        level.overlay === null ? [] : [level.overlay],
      ),
    ),
    "display hierarchy overlays",
  );
  if (
    analysisModels.length !== 3 ||
    analysisModels.map(({ id }) => id).join(",") !== "england_wales,northern_ireland,scotland"
  ) {
    throw new TypeError(
      "catalogue must contain England and Wales, Northern Ireland, and Scotland analysis models in order",
    );
  }
  const pointLayers = array(record.point_layers, "catalogue point layers").map(parsePointLayer);
  unique(
    pointLayers.map(({ id }) => id),
    "point-layer IDs",
  );
  const electoralRegisters = array(
    record.electoral_registers,
    "catalogue electoral registers",
  ).map(parseElectoralRegister);
  unique(
    electoralRegisters.map(({ id }) => id),
    "electoral-register IDs",
  );
  const baseAreaCount = unsignedInteger(record.base_area_count, "base-area count");
  if (
    analysisModels.reduce((total, model) => total + model.baseAreaCount, 0) !== baseAreaCount
  ) {
    throw new TypeError("analysis model base-area counts do not match the catalogue total");
  }
  const mapMin = numberPair(record.map_min, "map minimum");
  const mapMax = numberPair(record.map_max, "map maximum");
  if (mapMin[0] >= mapMax[0] || mapMin[1] >= mapMax[1]) {
    throw new TypeError("catalogue map bounds are reversed or empty");
  }
  return {
    apiVersion: unsignedInteger(record.api_version, "API version"),
    dataVersion: unsignedInteger(record.data_version, "data version"),
    baseAreaCount,
    mapMin,
    mapMax,
    mapTiles: parseMapTileSpec(record.map_tiles),
    basemap: parseBasemap(record.basemap),
    analysisModels,
    pointLayers,
    electoralRegisters,
    operationalPresets: array(record.operational_presets, "operational presets").map(
      parseOperationalPresetDefinition,
    ),
  };
}

function parseAnalysisModel(value: unknown): AnalysisModelCatalog {
  const record = exactObject(value, "analysis model", [
    "id",
    "label",
    "display_hierarchy",
    "base_area_count",
    "statistics",
    "initial_statistic_id",
    "default_affinity_recipe",
  ]);
  const id = parseAnalysisModelId(record.id);
  const displayHierarchy = array(record.display_hierarchy, `${id} display hierarchy`).map(
    (level, index) => {
      const parsed = exactObject(level, `${id} display hierarchy level ${index}`, [
        "overlay",
        "min_zoom",
      ]);
      return {
        overlay: nullableValue(parsed.overlay, (value) => parseOverlayId(value)),
        minZoom: unsignedInteger(parsed.min_zoom, "display hierarchy minimum zoom", 24),
      };
    },
  );
  if (
    displayHierarchy.length === 0 ||
    displayHierarchy.some(
      (level, index) =>
        index < displayHierarchy.length - 1 !== (level.overlay !== null) ||
        (index > 0 && level.minZoom <= (displayHierarchy[index - 1]?.minZoom ?? -1)),
    )
  ) {
    throw new TypeError(`${id} display hierarchy is invalid`);
  }
  const statistics = array(record.statistics, `${id} statistics`).map(parseStatistic);
  unique(
    statistics.map((statistic) => statistic.id),
    `${id} statistic IDs`,
  );
  const initialStatisticId = string(record.initial_statistic_id, "initial statistic ID", true);
  if (!statistics.some((statistic) => statistic.id === initialStatisticId)) {
    throw new TypeError(`${id} initial statistic is absent from the catalogue`);
  }
  return {
    id,
    label: string(record.label, "analysis model label", true),
    displayHierarchy,
    baseAreaCount: positiveInteger(
      record.base_area_count,
      "analysis model base-area count",
      0xffff_ffff,
    ),
    statistics,
    initialStatisticId,
    defaultAffinityRecipe: parseAffinityRecipe(record.default_affinity_recipe),
  };
}

export function parseMapMetadataResponse(value: unknown): MapMetadataResponse {
  const record = exactObject(value, "map metadata", [
    "model",
    "base_area_map_indices",
    "base_area_km2",
    "memberships",
    "median_ages",
  ]);
  const memberships = exactObject(record.memberships, "map memberships", [
    "lsoas",
    "msoas",
    "council_wards",
    "county_divisions",
    "constituencies",
    "settlements",
    "scotland_data_zones",
    "scotland_intermediate_zones",
    "northern_ireland_super_data_zones",
  ]);
  const parseMemberships = (value: unknown, name: string) =>
    array(value, name).map((indices, areaIndex) =>
      array(indices, `${name} ${areaIndex}`).map((index) =>
        unsignedInteger(index, `${name} base-area index`, 0xffff_ffff),
      ),
    );
  const baseAreaMapIndices = array(record.base_area_map_indices, "base-area map indices").map(
    (index) => unsignedInteger(index, "base-area map index", 0xffff_ffff),
  );
  unique(baseAreaMapIndices, "base-area map indices");
  const baseAreaKm2 = array(record.base_area_km2, "base-area areas").map((area) => {
    const value = finiteNumber(area, "base-area area");
    if (value <= 0) throw new TypeError("base-area area must be positive");
    return value;
  });
  aligned(baseAreaMapIndices.length, baseAreaKm2.length, "base-area areas");
  const parsedMemberships = {
    lsoas: parseMemberships(memberships.lsoas, "LSOA memberships"),
    msoas: parseMemberships(memberships.msoas, "MSOA memberships"),
    council_wards: parseMemberships(memberships.council_wards, "council ward memberships"),
    county_divisions: parseMemberships(
      memberships.county_divisions,
      "county division memberships",
    ),
    constituencies: parseMemberships(memberships.constituencies, "constituency memberships"),
    settlements: parseMemberships(memberships.settlements, "settlement memberships"),
    scotland_data_zones: parseMemberships(
      memberships.scotland_data_zones,
      "Scottish Data Zone memberships",
    ),
    scotland_intermediate_zones: parseMemberships(
      memberships.scotland_intermediate_zones,
      "Scottish Intermediate Zone memberships",
    ),
    northern_ireland_super_data_zones: parseMemberships(
      memberships.northern_ireland_super_data_zones,
      "Northern Irish Super Data Zone memberships",
    ),
  };
  for (const [geography, groups] of Object.entries(parsedMemberships)) {
    if (groups.some((indices) => indices.some((index) => index >= baseAreaKm2.length))) {
      throw new TypeError(`${geography} membership contains an out-of-range base-area index`);
    }
  }
  return {
    model: parseAnalysisModelId(record.model),
    baseAreaMapIndices,
    baseAreaKm2,
    memberships: parsedMemberships,
    medianAges: parseMedianAgeMetadata(
      record.median_ages,
      baseAreaKm2.length,
      parsedMemberships,
    ),
  };
}

function parseMedianAgeMetadata(
  value: unknown,
  baseAreaCount: number,
  memberships: MapMetadataResponse["memberships"],
): MedianAgeMetadata {
  const record = exactObject(value, "median ages", [
    "census_year",
    "band_width_years",
    "top_coded_age",
    "base_areas",
    "overlays",
  ]);
  const topCodedAge = positiveInteger(record.top_coded_age, "top-coded age", 150);
  const parseAges = (value: unknown, length: number, name: string) => {
    const ages = array(value, name).map((age) =>
      nullableValue(age, (value) => {
        const years = finiteNumber(value, name);
        if (years < 0 || years > topCodedAge) throw new TypeError(`${name} is out of range`);
        return years;
      }),
    );
    aligned(length, ages.length, name);
    return ages;
  };
  const keys = Object.keys(memberships) as OverlayId[];
  const rawOverlays = exactObject(record.overlays, "median age overlays", keys);
  const parseOverlay = (key: OverlayId) =>
    parseAges(rawOverlays[key], memberships[key].length, `${key} median ages`);
  const overlays = {
    lsoas: parseOverlay("lsoas"),
    msoas: parseOverlay("msoas"),
    council_wards: parseOverlay("council_wards"),
    county_divisions: parseOverlay("county_divisions"),
    constituencies: parseOverlay("constituencies"),
    settlements: parseOverlay("settlements"),
    scotland_data_zones: parseOverlay("scotland_data_zones"),
    scotland_intermediate_zones: parseOverlay("scotland_intermediate_zones"),
    northern_ireland_super_data_zones: parseOverlay("northern_ireland_super_data_zones"),
  };
  return {
    censusYear: positiveInteger(record.census_year, "age census year", 9999),
    bandWidthYears: positiveInteger(record.band_width_years, "age band width", topCodedAge),
    topCodedAge,
    baseAreas: parseAges(record.base_areas, baseAreaCount, "base-area median ages"),
    overlays,
  };
}

export function parseElectoralRegisterBootstrapResponse(
  value: unknown,
): ElectoralRegisterBootstrapResponse {
  const record = exactObject(value, "electoral-register bootstrap", [
    "elector_counts_by_base_area",
  ]);
  return {
    electorCountsByBaseArea: array(record.elector_counts_by_base_area, "elector counts").map(
      (candidate) => unsignedInteger(candidate, "elector count", 0xffff_ffff),
    ),
  };
}

export function parseMetricResponse(value: unknown): MetricResponse {
  const record = exactObject(value, "metric", [
    "model",
    "label",
    "description",
    "percentiles",
    "source_contexts",
    "aggregation_inputs",
    "operational_inputs",
    "completion_hours",
  ]);
  const percentiles = nullableNumbers(record.percentiles, "metric percentiles");
  for (const percentile of percentiles) {
    if (percentile !== null && (percentile < 0 || percentile > 100)) {
      throw new TypeError("metric percentile must be between 0 and 100");
    }
  }
  const sourceContexts = nullableArray(
    record.source_contexts,
    "metric source contexts",
    nullable(parseSourceContext),
  );
  const aggregationInputs = nullableValue(record.aggregation_inputs, parseAggregationInputs);
  const operationalInputs = nullableArray(
    record.operational_inputs,
    "metric operational inputs",
    parseOperationalInput,
  );
  const completionHours = nullableArray(
    record.completion_hours,
    "metric completion hours",
    nullable((candidate) => finiteNumber(candidate, "completion hours")),
  );
  if (sourceContexts !== null)
    aligned(percentiles.length, sourceContexts.length, "source contexts");
  if (aggregationInputs !== null) {
    aligned(percentiles.length, aggregationInputs.numerators.length, "aggregation numerators");
    if (aggregationInputs.denominators !== null) {
      aligned(
        percentiles.length,
        aggregationInputs.denominators.length,
        "aggregation denominators",
      );
    }
  }
  if (operationalInputs !== null) {
    aligned(percentiles.length, operationalInputs.length, "operational inputs");
  }
  if (completionHours !== null) {
    aligned(percentiles.length, completionHours.length, "completion hours");
  }
  return {
    model: parseAnalysisModelId(record.model),
    label: string(record.label, "metric label", true),
    description: string(record.description, "metric description"),
    percentiles,
    sourceContexts,
    aggregationInputs,
    operationalInputs,
    completionHours,
  };
}

function parseAnalysisModelId(value: unknown): AnalysisModelId {
  return enumeration(
    value,
    ["england_wales", "northern_ireland", "scotland"] as const,
    "analysis model ID",
  );
}

function parseMapTileSpec(value: unknown): MapTileSpec {
  const record = exactObject(value, "map tile specification", ["min_zoom", "max_zoom"]);
  const minZoom = unsignedInteger(record.min_zoom, "map tile minimum zoom", 0xff);
  const maxZoom = unsignedInteger(record.max_zoom, "map tile maximum zoom", 0xff);
  if (minZoom > maxZoom) throw new TypeError("map tile zoom range is reversed");
  return { minZoom, maxZoom };
}

function parseBasemap(value: unknown): BasemapSpec {
  const record = exactObject(value, "basemap", [
    "file",
    "schema_version",
    "min_zoom",
    "max_zoom",
    "sha256",
  ]);
  string(record.file, "basemap file", true);
  unsignedInteger(record.schema_version, "basemap schema version", 0xffff_ffff);
  string(record.sha256, "basemap digest", true);
  const minZoom = unsignedInteger(record.min_zoom, "minimum basemap zoom", 0xff);
  const maxZoom = unsignedInteger(record.max_zoom, "maximum basemap zoom", 0xff);
  if (minZoom > maxZoom) throw new TypeError("basemap zoom range is reversed");
  return { minZoom, maxZoom };
}

function parseStatistic(value: unknown): StatisticSpec {
  const record = exactObject(value, "statistic", [
    "id",
    "table",
    "label",
    "file",
    "column",
    "denominator_column",
    "provenance",
  ]);
  string(record.column, "statistic column", true);
  return {
    id: string(record.id, "statistic ID", true),
    table: string(record.table, "statistic table", true),
    label: string(record.label, "statistic label", true),
    file: string(record.file, "statistic file", true),
    denominatorColumn: nullableString(record.denominator_column, "statistic denominator"),
    provenance: parseProvenance(record.provenance),
  };
}

function parseProvenance(value: unknown): StatisticProvenance {
  const base = object(value, "statistic provenance");
  const kind = string(base.kind, "statistic provenance kind", true);
  if (kind === "council_election") {
    const record = exactObject(value, "council-election provenance", ["kind", "geography"]);
    const geography = enumeration(
      record.geography,
      ["council_ward", "county_division"] as const,
      "council-election geography",
    );
    return { kind, geography };
  }
  exactKeys(base, "statistic provenance", ["kind"]);
  if (
    kind !== "ons_census" &&
    kind !== "nrs_census" &&
    kind !== "nisra_census" &&
    kind !== "uk_parliament" &&
    kind !== "eu_referendum"
  ) {
    throw new TypeError("statistic provenance kind is invalid");
  }
  return { kind };
}

function parsePointLayer(value: unknown): PointLayerSpec {
  const record = exactObject(value, "point layer", [
    "id",
    "label",
    "file",
    "schema_version",
    "record_count",
    "addressable_record_count",
    "geocoded_record_count",
    "location_count",
    "address_status_counts",
  ]);
  string(record.file, "point-layer file", true);
  unsignedInteger(record.schema_version, "point-layer schema version", 0xffff_ffff);
  const statuses = object(record.address_status_counts, "point-layer address statuses");
  for (const [name, count] of Object.entries(statuses)) {
    string(name, "address-status name", true);
    unsignedInteger(count, "address-status count");
  }
  return {
    id: string(record.id, "point-layer ID", true),
    label: string(record.label, "point-layer label", true),
    recordCount: unsignedInteger(record.record_count, "point-layer record count"),
    addressableRecordCount: unsignedInteger(
      record.addressable_record_count,
      "point-layer addressable count",
    ),
    geocodedRecordCount: unsignedInteger(
      record.geocoded_record_count,
      "point-layer geocoded record count",
    ),
    locationCount: unsignedInteger(record.location_count, "point-layer location count"),
  };
}

function parseElectoralRegister(value: unknown): ElectoralRegisterSpec {
  const record = exactObject(value, "electoral register", [
    "id",
    "label",
    "issuing_authority",
    "coverage",
    "effective_date",
    "file",
    "schema_version",
    "snapshot_id",
    "elector_count",
    "address_count",
    "geocoded_elector_count",
    "geocoded_address_count",
    "base_area_assigned_elector_count",
    "base_area_assigned_address_count",
    "usrn_assigned_elector_count",
    "usrn_assigned_address_count",
    "location_count",
  ]);
  const authority = exactObject(record.issuing_authority, "issuing authority", [
    "name",
    "identifier",
  ]);
  string(authority.name, "issuing-authority name", true);
  nullableString(authority.identifier, "issuing-authority identifier");
  array(record.coverage, "electoral-register coverage").forEach((candidate) => {
    const coverage = exactObject(candidate, "electoral-register coverage item", [
      "geography",
      "code",
      "label",
    ]);
    string(coverage.geography, "coverage geography", true);
    string(coverage.code, "coverage code", true);
    string(coverage.label, "coverage label", true);
  });
  const effectiveDate = nullableString(record.effective_date, "register effective date");
  if (effectiveDate !== null && !/^\d{4}-\d{2}-\d{2}$/.test(effectiveDate)) {
    throw new TypeError("register effective date is invalid");
  }
  string(record.file, "electoral-register file", true);
  unsignedInteger(record.schema_version, "electoral-register schema version", 0xffff_ffff);
  string(record.snapshot_id, "electoral-register snapshot ID", true);
  const id = string(record.id, "electoral-register ID", true);
  if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(id) || id.length > 64) {
    throw new TypeError("electoral-register ID is invalid");
  }
  return {
    id,
    label: string(record.label, "electoral-register label", true),
    electorCount: unsignedInteger(record.elector_count, "elector count", 0xffff_ffff),
    addressCount: unsignedInteger(record.address_count, "register address count", 0xffff_ffff),
    geocodedElectorCount: unsignedInteger(
      record.geocoded_elector_count,
      "geocoded elector count",
      0xffff_ffff,
    ),
    geocodedAddressCount: unsignedInteger(
      record.geocoded_address_count,
      "geocoded register address count",
      0xffff_ffff,
    ),
    baseAreaAssignedElectorCount: unsignedInteger(
      record.base_area_assigned_elector_count,
      "base area-assigned elector count",
      0xffff_ffff,
    ),
    baseAreaAssignedAddressCount: unsignedInteger(
      record.base_area_assigned_address_count,
      "base area-assigned address count",
      0xffff_ffff,
    ),
    usrnAssignedElectorCount: unsignedInteger(
      record.usrn_assigned_elector_count,
      "USRN-assigned elector count",
      0xffff_ffff,
    ),
    usrnAssignedAddressCount: unsignedInteger(
      record.usrn_assigned_address_count,
      "USRN-assigned address count",
      0xffff_ffff,
    ),
    locationCount: unsignedInteger(record.location_count, "register location count"),
  };
}

function parseOperationalPresetDefinition(value: unknown): OperationalPresetDefinition {
  const record = exactObject(value, "operational preset", ["preset", "label", "parameters"]);
  return {
    preset: enumeration(
      record.preset,
      ["canvassing", "leafletting"] as const,
      "operational preset",
    ),
    label: string(record.label, "operational-preset label", true),
    parameters: parseOperationalModelParams(record.parameters),
  };
}

function parseSourceContext(value: unknown): StatisticSourceContext {
  const record = exactObject(value, "statistic source context", [
    "source_area_code",
    "source_area_name",
    "source_authority_name",
    "source_election_date",
    "source_contributor_count",
    "dominant_source_weight",
  ]);
  return {
    sourceAreaCode: string(record.source_area_code, "source area code", true),
    sourceAreaName: string(record.source_area_name, "source area name", true),
    sourceAuthorityName: string(record.source_authority_name, "source authority name"),
    sourceElectionDate: string(record.source_election_date, "source election date", true),
    sourceContributorCount: unsignedInteger(
      record.source_contributor_count,
      "source contributor count",
      0xffff_ffff,
    ),
    dominantSourceWeight: finiteNumber(record.dominant_source_weight, "dominant source weight"),
  };
}

function parseAggregationInputs(value: unknown): StatisticAggregationInputs {
  const record = exactObject(value, "statistic aggregation inputs", [
    "numerators",
    "denominators",
  ]);
  return {
    numerators: nullableNumbers(record.numerators, "aggregation numerators"),
    denominators:
      record.denominators === null
        ? null
        : nullableNumbers(record.denominators, "aggregation denominators"),
  };
}

function parseOperationalInput(value: unknown): OperationalInputRow {
  const record = exactObject(value, "operational input", [
    "affinity_percentile",
    "household_count",
    "household_density_per_km2",
  ]);
  const affinityPercentile = nullableNumber(record.affinity_percentile, "affinity percentile");
  if (affinityPercentile !== null && (affinityPercentile < 0 || affinityPercentile > 100)) {
    throw new TypeError("affinity percentile must be between 0 and 100");
  }
  return {
    affinityPercentile,
    householdCount: nullableNumber(record.household_count, "household count"),
    householdDensityPerKm2: nullableNumber(
      record.household_density_per_km2,
      "household density",
    ),
  };
}

function parseAffinityRecipe(value: unknown): AffinityRecipe {
  const record = exactObject(value, "affinity recipe", ["groups"]);
  return {
    groups: array(record.groups, "affinity groups").map((candidate) => {
      const group = exactObject(candidate, "affinity group", [
        "name",
        "weight_percent",
        "subgroups",
      ]);
      if (!isAffinityWeight(group.weight_percent)) {
        throw new TypeError("affinity-group weight must be an integer between -100 and 100");
      }
      return {
        name: string(group.name, "affinity-group name"),
        weightPercent: group.weight_percent,
        subgroups: array(group.subgroups, "affinity subgroups").map((subgroupValue) => {
          const subgroup = exactObject(subgroupValue, "affinity subgroup", [
            "enabled",
            "statistic_ids",
          ]);
          return {
            enabled: boolean(subgroup.enabled, "affinity subgroup enabled flag"),
            statisticIds: array(subgroup.statistic_ids, "affinity subgroup statistic IDs").map(
              (candidate) => string(candidate, "affinity statistic ID", true),
            ),
          };
        }),
      };
    }),
  };
}

function parseOperationalModelParams(value: unknown): OperationalModelParams {
  const record = exactObject(value, "operational parameters", [
    "service_seconds_per_household",
    "walking_seconds_at_reference_density",
    "reference_density_households_per_km2",
    "fixed_setup_seconds_per_base_area",
    "affinity_contrast_p90_p10",
    "minimum_effective_density_households_per_km2",
    "maximum_effective_density_households_per_km2",
  ]);
  return {
    serviceSecondsPerHousehold: finiteNumber(
      record.service_seconds_per_household,
      "service seconds per household",
    ),
    walkingSecondsAtReferenceDensity: finiteNumber(
      record.walking_seconds_at_reference_density,
      "walking seconds at reference density",
    ),
    referenceDensityHouseholdsPerKm2: finiteNumber(
      record.reference_density_households_per_km2,
      "reference household density",
    ),
    fixedSetupSecondsPerBaseArea: finiteNumber(
      record.fixed_setup_seconds_per_base_area,
      "fixed setup seconds per base area",
    ),
    affinityContrastP90P10: finiteNumber(record.affinity_contrast_p90_p10, "affinity contrast"),
    minimumEffectiveDensityHouseholdsPerKm2: finiteNumber(
      record.minimum_effective_density_households_per_km2,
      "minimum effective household density",
    ),
    maximumEffectiveDensityHouseholdsPerKm2: finiteNumber(
      record.maximum_effective_density_households_per_km2,
      "maximum effective household density",
    ),
  };
}

function exactObject(
  value: unknown,
  name: string,
  keys: readonly string[],
): Record<string, unknown> {
  const record = object(value, name);
  exactKeys(record, name, keys);
  return record;
}

function exactKeys(
  record: Record<string, unknown>,
  name: string,
  keys: readonly string[],
): void {
  const expected = new Set(keys);
  for (const key of keys) {
    if (!Object.hasOwn(record, key)) throw new TypeError(`${name} is missing ${key}`);
  }
  for (const key of Object.keys(record)) {
    if (!expected.has(key)) throw new TypeError(`${name} contains unknown field ${key}`);
  }
}

function object(value: unknown, name: string): Record<string, unknown> {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    throw new TypeError(`${name} must be an object`);
  }
  return value as Record<string, unknown>;
}

function array(value: unknown, name: string): readonly unknown[] {
  if (!Array.isArray(value)) throw new TypeError(`${name} must be an array`);
  return value;
}

function nullableArray<T>(
  value: unknown,
  name: string,
  parser: (candidate: unknown) => T,
): readonly T[] | null {
  return value === null ? null : array(value, name).map(parser);
}

function nullable<T>(parser: (candidate: unknown) => T): (candidate: unknown) => T | null {
  return (candidate) => (candidate === null ? null : parser(candidate));
}

function nullableValue<T>(value: unknown, parser: (candidate: unknown) => T): T | null {
  return value === null ? null : parser(value);
}

function string(value: unknown, name: string, nonEmpty = false): string {
  if (typeof value !== "string" || (nonEmpty && value.length === 0)) {
    throw new TypeError(`${name} must be ${nonEmpty ? "a non-empty" : "a"} string`);
  }
  return value;
}

function nullableString(value: unknown, name: string): string | null {
  return value === null ? null : string(value, name, true);
}

function boolean(value: unknown, name: string): boolean {
  if (typeof value !== "boolean") throw new TypeError(`${name} must be a boolean`);
  return value;
}

function finiteNumber(value: unknown, name: string): number {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    throw new TypeError(`${name} must be a finite number`);
  }
  return value;
}

function nullableNumber(value: unknown, name: string): number | null {
  return value === null ? null : finiteNumber(value, name);
}

function nullableNumbers(value: unknown, name: string): readonly (number | null)[] {
  return array(value, name).map((candidate) => nullableNumber(candidate, name));
}

function unsignedInteger(
  value: unknown,
  name: string,
  maximum = Number.MAX_SAFE_INTEGER,
): number {
  if (!Number.isSafeInteger(value) || (value as number) < 0 || (value as number) > maximum) {
    throw new TypeError(`${name} must be an unsigned integer`);
  }
  return value as number;
}

function positiveInteger(value: unknown, name: string, maximum: number): number {
  const parsed = unsignedInteger(value, name, maximum);
  if (parsed === 0) throw new TypeError(`${name} must be positive`);
  return parsed;
}

function numberPair(value: unknown, name: string): readonly [number, number] {
  const values = array(value, name);
  if (values.length !== 2) throw new TypeError(`${name} must contain two numbers`);
  return [finiteNumber(values[0], name), finiteNumber(values[1], name)];
}

function enumeration<const T extends readonly string[]>(
  value: unknown,
  allowed: T,
  name: string,
): T[number] {
  if (typeof value !== "string" || !allowed.includes(value)) {
    throw new TypeError(`${name} is invalid`);
  }
  return value as T[number];
}

function unique<T>(values: readonly T[], name: string): void {
  if (new Set(values).size !== values.length) throw new TypeError(`${name} must be unique`);
}

function aligned(expected: number, actual: number, name: string): void {
  if (actual !== expected) {
    throw new TypeError(`${name} has ${actual} rows; expected ${expected}`);
  }
}
